import { sharedLib } from "@shared/services/shared.service.ts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { displayResultMessage } from "utils/misc.tsx";
import {
  AssetManagerConfigurationType,
  LanguageNameSpaces,
} from "@shared/types";
import { useExchangesMetadata } from "@shared/utils/hooks/misc.ts";
import { INPUT_MAX_LENGTH } from "features/assetManager/BusinessProfile/constants.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { DEFAULT_LANGUAGE, isSupportedLanguage } from "utils/language.ts";

const useBusinessProfile = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ASSET_MANAGER);
  const { exchanges: allExchanges } = useExchangesMetadata();

  const { data, isLoading } =
    sharedLib.sharedApi.settings.useGetAssetManagerConfigurationQuery();
  const [setConfiguration, { isLoading: setConfigurationLoading }] =
    sharedLib.sharedApi.settings.useSetAssetManagerConfigurationMutation();

  const [tabTitle, setTabTitle] = useState<string>();
  const [displayName, setDisplayName] = useState<string>();
  const [language, setLanguage] = useState(DEFAULT_LANGUAGE);
  const [lightModePrimaryColor, setLightModePrimaryColor] = useState<
    string | null
  >(null);
  const [darkModePrimaryColor, setDarkModePrimaryColor] = useState<
    string | null
  >(null);
  const [supportedExchanges, setSupportedExchanges] = useState<string[] | null>(
    []
  );
  const [includeNewExchanges, setIncludeNewExchanges] = useState(false);

  const allSelected = useMemo(
    () =>
      allExchanges?.every((exchange) => supportedExchanges?.includes(exchange)),
    [allExchanges, supportedExchanges]
  );

  const tabTitleError = !!tabTitle && tabTitle.length > INPUT_MAX_LENGTH;
  const displayNameError =
    !!displayName && displayName.length > INPUT_MAX_LENGTH;
  const supportedExchangesError =
    !isLoading && !!supportedExchanges && supportedExchanges?.length < 1;
  const disabled = tabTitleError || displayNameError || supportedExchangesError;
  const allSupportedExchanges = allSelected && includeNewExchanges;

  useEffect(() => {
    if (!allSelected) {
      setIncludeNewExchanges(false);
    }
  }, [allSelected]);

  const handleSetSupportedExchanges = useCallback(
    (selectedExchanges: string[] | null) => {
      if (selectedExchanges === null) {
        setSupportedExchanges(allExchanges);
        setIncludeNewExchanges(true);
      } else {
        setSupportedExchanges(selectedExchanges);
      }
    },
    [allExchanges]
  );

  useEffect(() => {
    if (data) {
      setTabTitle(data.tab_title);
      setDisplayName(data.display_name);
      setLanguage(
        isSupportedLanguage(data.language) ? data.language : DEFAULT_LANGUAGE
      );
      setLightModePrimaryColor(data.light_mode_primary_color);
      setDarkModePrimaryColor(data.dark_mode_primary_color);
      handleSetSupportedExchanges(data.supported_exchanges);
    }
  }, [data, handleSetSupportedExchanges]);

  const onSubmit = () => {
    if (disabled) return;

    displayResultMessage(
      setConfiguration({
        tab_title: tabTitle && tabTitle.length > 0 ? tabTitle : null,
        display_name:
          displayName && displayName.length > 0 ? displayName : null,
        language,
        supported_exchanges: allSupportedExchanges ? null : supportedExchanges,
        light_mode_primary_color: lightModePrimaryColor,
        dark_mode_primary_color: darkModePrimaryColor,
      }).unwrap(),
      t("configuration.messages.success.set_configuration")
    )
      .then((data: AssetManagerConfigurationType) => {
        setTabTitle(data.tab_title);
        setDisplayName(data.display_name);
        setLightModePrimaryColor(data.light_mode_primary_color);
        setDarkModePrimaryColor(data.dark_mode_primary_color);
        handleSetSupportedExchanges(data.supported_exchanges);
      })
      .catch(() => {});
  };

  return {
    onSubmit,
    isLoading,
    allExchanges,
    allSelected,
    tabTitle,
    setTabTitle,
    displayName,
    setDisplayName,
    language,
    setLanguage,
    lightModePrimaryColor,
    setLightModePrimaryColor,
    darkModePrimaryColor,
    setDarkModePrimaryColor,
    supportedExchanges,
    setSupportedExchanges,
    setConfigurationLoading,
    disabled,
    tabTitleError,
    displayNameError,
    supportedExchangesError,
    includeNewExchanges,
    setIncludeNewExchanges,
    allSupportedExchanges,
  };
};

export default useBusinessProfile;
